import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';
import { ApiError } from '@/shared/types/api.types';
import { authService } from '../services/authService';
import type {
  AuthState,
  AuthenticatedUser,
  EditableUserDetails,
  LoginCredentials,
  UserRole,
} from '../types/auth.types';

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  // Starts true so guarded routes wait for the restore attempt instead of
  // bouncing a signed-in user to the login page on first paint.
  isRestoringSession: true,
  isSigningIn: false,
  error: null,
};

function messageFor(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Sign in failed. Please try again.';
}

export const login = createAsyncThunk<AuthenticatedUser, LoginCredentials, { rejectValue: string }>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      return await authService.login(credentials);
    } catch (error) {
      return rejectWithValue(messageFor(error));
    }
  }
);

export const restoreSession = createAsyncThunk<AuthenticatedUser | null, void, { rejectValue: string }>(
  'auth/restoreSession',
  async (_unused, { rejectWithValue }) => {
    try {
      return await authService.restoreSession();
    } catch (error) {
      return rejectWithValue(messageFor(error));
    }
  }
);

export const logout = createAsyncThunk<void>('auth/logout', () => authService.logout());

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /** Invoked by the API client when a token refresh fails mid-session. */
    sessionExpired: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.error = 'Your session has expired. Please sign in again.';
    },
    errorCleared: (state) => {
      state.error = null;
    },
    /**
     * Applies a saved profile edit to the session user, so the header and any other
     * consumer of the session show the new name without a round trip.
     *
     * The saved record is trusted over the user embedded in the login response: the Core
     * service owns these fields, and the identity provider may not have picked the change up yet.
     */
    userDetailsChanged: (state, action: PayloadAction<EditableUserDetails>) => {
      if (state.user === null) return;

      state.user.givenName = action.payload.givenName;
      state.user.familyName = action.payload.familyName;
      state.user.phoneNumber = action.payload.phoneNumber;
      state.user.department = action.payload.department;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.isSigningIn = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isSigningIn = false;
        // The domain type is deeply readonly, which Immer's mutable draft rejects.
        // castDraft records that the value is stored wholesale and never mutated in place.
        state.user = castDraft(action.payload);
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.isSigningIn = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = action.payload ?? 'Sign in failed. Please try again.';
      })
      .addCase(restoreSession.pending, (state) => {
        state.isRestoringSession = true;
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.isRestoringSession = false;
        state.user = castDraft(action.payload);
        state.isAuthenticated = action.payload !== null;
        state.error = null;
      })
      .addCase(restoreSession.rejected, (state, action) => {
        state.isRestoringSession = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = action.payload ?? 'Unable to restore your session. Please try again.';
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
      });
  },
});

export const { sessionExpired, errorCleared, userDetailsChanged } = authSlice.actions;
export const authReducer = authSlice.reducer;

/** Slice shape used by selectors, avoiding a store import from inside the feature. */
interface AuthSliceRoot {
  readonly auth: AuthState;
}

export const selectAuthUser = (state: AuthSliceRoot): AuthenticatedUser | null => state.auth.user;
export const selectIsAuthenticated = (state: AuthSliceRoot): boolean => state.auth.isAuthenticated;
export const selectIsRestoringSession = (state: AuthSliceRoot): boolean => state.auth.isRestoringSession;
export const selectIsSigningIn = (state: AuthSliceRoot): boolean => state.auth.isSigningIn;
export const selectAuthError = (state: AuthSliceRoot): string | null => state.auth.error;

export const selectHasRole =
  (role: UserRole) =>
  (state: AuthSliceRoot): boolean =>
    state.auth.user?.roles.includes(role) ?? false;

export const selectIsAdmin = (state: AuthSliceRoot): boolean => {
  const roles = state.auth.user?.roles ?? [];
  return roles.includes('Admin') || roles.includes('SuperAdmin');
};
